import { Box, HStack, VStack } from '@chakra-ui/react';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { SFRPropertyStatus } from '@services/appsync/types';
import React from 'react';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from '../../../config';

type InvestCTAProps = {
  children?: React.ReactNode;
};

export const InvestCTA: React.FC<InvestCTAProps> = () => {
  const { property } = usePropertiesModel();

  const totalPropertyAmount = Number(property?.target_amount ?? 0);
  const currentAmountRaised = Number(property?.raised_amount?.total ?? 0);
  const fundAmountAvailable = totalPropertyAmount - currentAmountRaised;
  const fundedPercent = (currentAmountRaised / totalPropertyAmount) * 100;
  const fullyFunded = property?.sfr?.status.status === SFRPropertyStatus.SOLD_OUT;

  let totalInvestors = Number(property?.investor_count?.total);
  if (isNaN(totalInvestors)) totalInvestors = 0;

  return (
    <VStack gap={5} alignItems={'flex-start'} justifyItems={'flex-start'} w="full">
      {fullyFunded ? (
        <HStack alignItems={'flex-end'}>
          <TextBlock fontSize={'40px'} weight="bold" lineHeight={1}>
            {currency.format(totalPropertyAmount ?? 0, { maximumFractionDigits: 0 })}
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            RAISED
          </TextBlock>
        </HStack>
      ) : (
        <HStack alignItems={'flex-end'}>
          <TextBlock color="blue.500" fontSize={'40px'} weight="bold" lineHeight={1}>
            {currency.format(fundAmountAvailable ?? 0, { maximumFractionDigits: 0 })}
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            AVAILABLE
          </TextBlock>
        </HStack>
      )}
      <Box backgroundColor="gray.100" borderRadius={15} h={15} w={'full'}>
        <Box
          background={fullyFunded ? 'gray.500' : 'linear-gradient(270deg, #308EFF 0%, #034089 100%)'}
          borderRadius={15}
          h={15}
          minW={fundedPercent < 5 ? '5%' : '0%'}
          w={`${fundedPercent}%`}></Box>
      </Box>
      <HStack gap="32px">
        <HStack justifyItems={'flex-start'} alignItems={'flex-end'}>
          <TextBlock fontSize={'24px'} weight="bold" lineHeight={1}>
            {totalInvestors}
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            {Number(totalInvestors) === 1 ? 'INVESTOR' : 'INVESTORS'}
          </TextBlock>
        </HStack>
        <HStack justifyItems={'flex-start'} alignItems={'flex-end'}>
          <TextBlock fontSize={'24px'} weight="bold" lineHeight={1}>
            {fullyFunded ? 100 : fundedPercent.toFixed(0)}%
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            {'RAISED'}
          </TextBlock>
        </HStack>
      </HStack>
      {fullyFunded ? (
        <Box
          background={`gray.300`}
          borderRadius={'5px'}
          color={'white'}
          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
          fontSize={'18px'}
          lineHeight={'60px'}
          textAlign={'center'}
          w={'full'}>
          Fully Funded
        </Box>
      ) : (
        <Box
          as={Link}
          _hover={{
            background: `linear-gradient(270deg, #308EFF 0%, #308EFF 100%);`,
            cursor: 'pointer',
          }}
          background={`linear-gradient(270deg, #308EFF 0%, #034089 100%);`}
          borderRadius={'5px'}
          backgroundColor={'blue.500'}
          color={'white'}
          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
          fontSize={'18px'}
          lineHeight={'60px'}
          to={property?.slug ? ROUTES.checkoutSFR(property.slug) : ''}
          textAlign={'center'}
          w={'full'}>
          Invest in {property?.display_name}
        </Box>
      )}
    </VStack>
  );
};
